import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * DriveDesk Hub Presentation Mode — the general-purpose ⭐ Pro / ⭐⭐ Elite
 * tier indicator, reusing the exact same `--presentation` (Pro/orange)
 * and `--elite` (Elite/purple) tokens as the existing `PremiumBadge`
 * (sidebar nav), but with size + placement variants so tier badges can
 * show up anywhere a premium feature is mentioned — dashboard cards,
 * Reports, Business Pulse, Daily Brief, Insights, Communication Center,
 * feature lists — not just inside the nav. Demo-only by convention
 * (callers should gate rendering with `isDemoUser`).
 *
 * Sizes: "xs" (inline with text), "sm" (section/card headers), "md"
 * (dashboard card corners, hero callouts).
 * Tone: "quiet" (soft tint, safe for dense screens — default) or
 * "emphasis" (solid fill, use once per card/section max).
 * Placement: "inline" (default, flows with content) or "corner"
 * (absolutely positioned top-right — parent must be `relative`).
 */
const SIZE_CLASSES = {
  xs: "gap-1 rounded-full px-1.5 py-0.5 text-[10px] leading-none",
  sm: "gap-1 rounded-full px-2 py-0.5 text-xs",
  md: "gap-1.5 rounded-full px-2.5 py-1 text-sm",
};

const ICON_SIZE = {
  xs: "h-3 w-3",
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
};

const TONE_CLASSES = {
  pro: {
    quiet: "bg-[hsl(var(--presentation-soft))] text-[hsl(var(--presentation))] border border-[hsl(var(--presentation)/0.25)]",
    emphasis: "bg-[hsl(var(--presentation))] text-[hsl(var(--presentation-foreground))] border border-[hsl(var(--presentation)/0.35)]",
  },
  elite: {
    quiet: "bg-[hsl(var(--elite-soft))] text-[hsl(var(--elite))] border border-[hsl(var(--elite)/0.25)]",
    emphasis: "bg-[hsl(var(--elite))] text-[hsl(var(--elite-foreground))] border border-[hsl(var(--elite)/0.35)]",
  },
};

export const TierBadge = ({ tier, size = "sm", tone = "quiet", placement = "inline", className }) => {
  if (!tier) return null;
  const isElite = tier === "elite";
  const key = isElite ? "elite" : "pro";
  return (
    <span
      className={cn(
        "inline-flex items-center font-semibold whitespace-nowrap shrink-0",
        SIZE_CLASSES[size],
        TONE_CLASSES[key][tone],
        placement === "corner" && "absolute right-3 top-3 md:right-4 md:top-4",
        className
      )}
      data-testid={`tier-badge-${key}`}
      title={isElite ? "Elite package feature" : "Professional package feature"}
    >
      {/* ⭐ Professional (single star) / ⭐⭐ Elite (double star) — per the
          815 Digital Services tier-signaling convention. */}
      <span className="inline-flex items-center -space-x-0.5">
        <Star className={cn(ICON_SIZE[size], "fill-current")} />
        {isElite && <Star className={cn(ICON_SIZE[size], "fill-current")} />}
      </span>
      {isElite ? "Elite" : "Professional"}
    </span>
  );
};
